import { translateTemplateText } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import dayjs from 'dayjs';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { findCategoryByName } from '../../helpers/category';
import { getColor } from '../../helpers/color';
import { msToShortTime, toDueString } from '../../helpers/datetime';
import { getRepeatFromStr, getRepeatText } from '../../helpers/quest';
import { useCachedCategories } from '../../hooks/caching/category';
import { CreateQuestVoiceAction } from '../../types/voice';
import { CategoryIcon } from '../common/CategoryIcon';
import { CommonIcon } from '../common/CommonIcon';

interface ActionCardCreateQuestViewProps {
  action: CreateQuestVoiceAction;
}

const hintStyle = { fontSize: 11, color: 'var(--tg-theme-hint-color)' };

export const ActionCardCreateQuestView: FC<ActionCardCreateQuestViewProps> = ({ action }) => {
  const { t } = useTranslation();
  const categories = useCachedCategories();
  const category = useMemo(() => {
    const byId = action.categoryId ? categories.find((c) => c.id === action.categoryId) : undefined;
    const byName = action.categoryName ? findCategoryByName(categories, action.categoryName) : undefined;
    return byId ?? byName ?? categories[0] ?? null;
  }, [action, categories]);
  const dueStr = action.dueDateIso && dayjs(action.dueDateIso).isValid()
    ? toDueString(dayjs(action.dueDateIso))
    : null;
  const repeatRes = action.repeatQuery ? getRepeatFromStr(action.repeatQuery) : null;
  const repeatText = repeatRes ? getRepeatText(repeatRes[0]) : null;
  const hasDifficulty = !!(action.difficulty && action.difficulty !== 'NoEffort');

  return (
    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ minWidth: 0 }}>
      {category && (
        <Stack direction="row" alignItems="center" gap={0.5} flexShrink={0}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: 0.75,
              background: getColor(category.iconColor),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CategoryIcon
              icon={category.icon}
              iconColor={category.iconColor}
              customIcon={category.customIcon}
              type="filled"
              size={10}
              color="var(--tg-theme-text-color)"
            />
          </Box>
          <Text style={{ ...hintStyle, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {translateTemplateText(t, category.templateId, 'name', category.name)}
          </Text>
        </Stack>
      )}
      {dueStr && <Text style={hintStyle}>{dueStr}</Text>}
      {hasDifficulty && <Text style={hintStyle}>{action.difficulty}</Text>}
      {action.focusModeEnabled && (
        <Stack direction="row" alignItems="center" gap={0.25} flexShrink={0}>
            <CommonIcon type="filled" color="var(--tg-theme-hint-color)" icon="timer" size={12} />
            <Text style={hintStyle}>
              {action.timedIntervalMinutes
                ? msToShortTime(action.timedIntervalMinutes * 60 * 1000)
                : 'focus'}
            </Text>
          </Stack>
      )}
      {repeatText && (
        <Stack direction="row" alignItems="center" gap={0.25} flexShrink={0}>
            <CommonIcon type="filled" color="var(--tg-theme-hint-color)" icon="repeat" size={12} />
            <Text style={hintStyle}>{repeatText}</Text>
          </Stack>
      )}
    </Stack>
  );
};
